/**
 * System Store Defaults
 *
 * Premium "Command Center" styled configuration page for system-wide store defaults.
 * Features tabbed interface with dark fintech aesthetic, SmartScheduleEditor, and
 * premium controls.
 *
 * @module components
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  CircularProgress,
  Switch,
  Paper,
  InputAdornment,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Info as InfoIcon,
  LocalShipping as DeliveryIcon,
  ShoppingCart as CartIcon,
  AccessTime as ScheduleIcon,
  Inventory as OperationsIcon,
  NotificationsActive as AlertIcon,
  ArrowBack as BackIcon,
  Check as CheckIcon,
  Public as GlobalIcon,
} from '@mui/icons-material';
import Logger from '../utils/Logger';
import { authInstance } from '../services/httpClient';
import { SmartScheduleEditor, type ScheduleData } from '../pages/StoreCommandCenter/components/ui/SmartScheduleEditor';
import { DeliveryTierBuilder, DeliveryTier } from './DeliveryTierBuilder';
import { SHOW_SLA } from '../config/features';
import { APP_BAR_HEIGHT } from '../admin-theme';

// ============================================================================
// Types
// ============================================================================

interface UberVerificationSettings {
  signature?: {
    enabled: boolean;
    collect_signer_name?: boolean;
    collect_signer_relationship?: boolean;
  };
  picture?: boolean;
  pincode?: {
    enabled: boolean;
  };
  verificationMethod?: string;
}

// ============================================================================
// Constants
// ============================================================================

const colors = {
  bgDeepest: 'var(--canvas)',
  bgCard: 'var(--panel)',
  bgElevated: 'var(--raised)',
  bgRecessed: 'var(--sunk)',
  textPrimary: 'var(--tx)',
  textSecondary: 'var(--tx2)',
  textMuted: 'var(--tx3)',
  border: 'var(--line-soft)',
  successGlow: '#00FF88',
  errorGlow: '#FF003C',
  warning: '#FFB800',
  info: '#00B4FF',
};

/** Glassmorphism header style */
const glassHeaderStyle = {
  background: 'color-mix(in srgb, var(--panel) 86%, transparent)',
  backdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--line-soft)',
  position: 'sticky' as const,
  // Below the fixed 56px app bar — top: 0 hides it underneath.
  top: APP_BAR_HEIGHT,
  zIndex: 10,
};

// ============================================================================
// Premium UI Components
// ============================================================================

interface PlateToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function PlateToggle({ label, description, checked, onChange, disabled = false }: PlateToggleProps) {
  return (
    <Box
      onClick={() => !disabled && onChange(!checked)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderRadius: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        bgcolor: checked ? `${colors.successGlow}08` : colors.bgRecessed,
        border: `1px solid ${checked ? `${colors.successGlow}40` : colors.border}`,
        boxShadow: checked ? `0 0 15px ${colors.successGlow}15` : 'none',
        transition: 'all 0.3s',
        opacity: disabled ? 0.5 : 1,
        '&:hover': disabled
          ? {}
          : {
            bgcolor: checked ? `${colors.successGlow}12` : colors.bgElevated,
            borderColor: checked ? `${colors.successGlow}60` : colors.textMuted,
          },
      }}
    >
      <Box>
        <Typography variant="body2" fontWeight={600} sx={{ color: checked ? colors.textPrimary : colors.textSecondary }}>
          {label}
        </Typography>
        {description && (
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {description}
          </Typography>
        )}
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: colors.successGlow,
            '& + .MuiSwitch-track': { bgcolor: `${colors.successGlow}60` },
          },
          '& .MuiSwitch-track': { bgcolor: colors.border },
        }}
      />
    </Box>
  );
}

interface DefaultsColumn {
  title: string;
  rows: Array<{ label: string; tag?: string; description: string }>;
}

/** One courier's read-only verification defaults: an info line, three step columns, a note. */
function DeliveryDefaults({ title, info, columns, note }: { title: string; info: string; columns: DefaultsColumn[]; note: React.ReactNode }) {
  return (
    <Paper sx={{ p: 3, bgcolor: colors.bgCard, borderRadius: 3, border: `1px solid ${colors.border}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <DeliveryIcon sx={{ color: colors.errorGlow }} />
        <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
          {title}
        </Typography>
      </Box>

      <Alert
        severity="info"
        sx={{
          mb: 3,
          bgcolor: `${colors.info}08`,
          color: colors.textSecondary,
          border: `1px solid ${colors.info}20`,
          '& .MuiAlert-icon': { color: colors.info },
        }}
      >
        {info}
      </Alert>

      <Grid container spacing={3}>
        {columns.map((col) => (
          <Grid key={col.title} size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, bgcolor: colors.bgRecessed, borderRadius: 2, border: `1px solid ${colors.border}` }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: colors.textPrimary, mb: 2 }}>
                {col.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {col.rows.map((row) => (
                  <PlateToggle
                    key={row.label}
                    label={row.tag ? `${row.label} (${row.tag})` : row.label}
                    description={row.description}
                    checked={true}
                    onChange={() => { }}
                    disabled
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Alert
        severity="warning"
        sx={{
          mt: 3,
          bgcolor: `${colors.warning}08`,
          color: colors.textSecondary,
          border: `1px solid ${colors.warning}20`,
          '& .MuiAlert-icon': { color: colors.warning },
        }}
      >
        {note}
      </Alert>
    </Paper>
  );
}

interface PremiumInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'time';
  prefix?: string;
  suffix?: string;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  min?: number;
  step?: number;
}

function PremiumInput({
  label,
  value,
  onChange,
  type = 'text',
  prefix,
  suffix,
  helperText,
  disabled = false,
  fullWidth = true,
  min,
  step,
}: PremiumInputProps) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 500, mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <TextField
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        fullWidth={fullWidth}
        size="small"
        InputProps={{
          startAdornment: prefix ? (
            <InputAdornment position="start">
              <Typography sx={{ color: colors.textSecondary }}>{prefix}</Typography>
            </InputAdornment>
          ) : undefined,
          endAdornment: suffix ? (
            <InputAdornment position="end">
              <Typography sx={{ color: colors.textSecondary }}>{suffix}</Typography>
            </InputAdornment>
          ) : undefined,
        }}
        inputProps={{ min, step }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: colors.bgRecessed,
            color: colors.textPrimary,
            fontWeight: 600,
            '& fieldset': { borderColor: colors.border },
            '&:hover fieldset': { borderColor: colors.info },
            '&.Mui-focused fieldset': { borderColor: colors.info },
          },
          '& .MuiInputBase-input': {
            fontFamily: 'monospace',
          },
        }}
      />
      {helperText && (
        <Typography variant="caption" sx={{ color: colors.textMuted, mt: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

function SaveButton({ onClick, saving, label }: { onClick: () => void; saving: boolean; label: string }) {
  return (
    <Button
      variant="contained"
      onClick={onClick}
      disabled={saving}
      startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
      sx={{
        bgcolor: colors.errorGlow,
        color: '#000',
        fontWeight: 600,
        '&:hover': { bgcolor: '#FF3366' },
        '&.Mui-disabled': { bgcolor: colors.border, color: colors.textMuted },
      }}
    >
      {saving ? 'Saving...' : label}
    </Button>
  );
}

// ============================================================================
// Tab Panel Component
// ============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
      {value === index && children}
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SystemDefaultsEdit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Pricing & Cart state
  const [deliveryTiers, setDeliveryTiers] = useState<DeliveryTier[]>([]);

  // Schedule state (for SmartScheduleEditor)
  const [schedule, setSchedule] = useState<ScheduleData>({});
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Uber Delivery Defaults state
  const [uberDropoffDefaults, setUberDropoffDefaults] = useState<UberVerificationSettings | null>(null);
  const [uberReturnDefaults, setUberReturnDefaults] = useState<UberVerificationSettings | null>(null);

  // Alert settings state
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [pickingSlaMinutes, setPickingSlaMinutes] = useState('10');
  const [packingSlaMinutes, setPackingSlaMinutes] = useState('15');
  const [pickupSlaMinutes, setPickupSlaMinutes] = useState('30');

  // Cut Offs: minutes before closing that every store stops taking orders (Jason, 2 Nov 2026).
  const [checkoutCutoffMinutes, setCheckoutCutoffMinutes] = useState('30');
  const [savingCutoffs, setSavingCutoffs] = useState(false);

  useEffect(() => {
    fetchDefaults();
  }, []);

  /**
   * Transform backend data to SmartScheduleEditor format
   */
  const transformToScheduleData = (
    workingHours: Array<{ dayOfWeek: number; openTime: string; closeTime: string }>,
    earlyCutoffs: Array<{ dayOfWeek: number; cutoffTime: string; startTime?: string }>
  ): ScheduleData => {
    const scheduleData: ScheduleData = {};
    const cutoffMap = new Map(earlyCutoffs.map(ec => [ec.dayOfWeek, ec.cutoffTime]));
    const startTimeMap = new Map(earlyCutoffs.map(ec => [ec.dayOfWeek, ec.startTime]));

    for (let day = 0; day < 7; day++) {
      scheduleData[day] = {
        openTime: '',
        closeTime: '',
        liquorCutoff: cutoffMap.get(day)?.substring(0, 5) || null,
        liquorStartTime: startTimeMap.get(day)?.substring(0, 5) || null,
      };
    }

    for (const wh of workingHours) {
      if (scheduleData[wh.dayOfWeek] && !scheduleData[wh.dayOfWeek].openTime) {
        scheduleData[wh.dayOfWeek].openTime = wh.openTime?.substring(0, 5) || '';
        scheduleData[wh.dayOfWeek].closeTime = wh.closeTime?.substring(0, 5) || '';
      }
    }

    return scheduleData;
  };

  /**
   * Transform SmartScheduleEditor format back to backend format
   */
  const transformFromScheduleData = (scheduleData: ScheduleData): {
    workingHours: Array<{ dayOfWeek: number; slotNumber: number; openTime: string; closeTime: string }>;
    earlyCutoffs: Array<{ dayOfWeek: number; cutoffTime: string; startTime?: string }>;
  } => {
    const workingHours: Array<{ dayOfWeek: number; slotNumber: number; openTime: string; closeTime: string }> = [];
    const earlyCutoffs: Array<{ dayOfWeek: number; cutoffTime: string; startTime?: string }> = [];

    for (let day = 0; day < 7; day++) {
      const daySchedule = scheduleData[day];
      if (daySchedule) {
        if (daySchedule.openTime && daySchedule.closeTime) {
          workingHours.push({
            dayOfWeek: day,
            slotNumber: 1,
            openTime: daySchedule.openTime.length === 5 ? `${daySchedule.openTime}:00` : daySchedule.openTime,
            closeTime: daySchedule.closeTime.length === 5 ? `${daySchedule.closeTime}:00` : daySchedule.closeTime,
          });
        }
        if (daySchedule.liquorCutoff || daySchedule.liquorStartTime) {
          const entry: { dayOfWeek: number; cutoffTime: string; startTime?: string } = {
            dayOfWeek: day,
            cutoffTime: daySchedule.liquorCutoff
              ? (daySchedule.liquorCutoff.length === 5 ? `${daySchedule.liquorCutoff}:00` : daySchedule.liquorCutoff)
              : '',
          };
          if (daySchedule.liquorStartTime) {
            entry.startTime = daySchedule.liquorStartTime.length === 5
              ? `${daySchedule.liquorStartTime}:00`
              : daySchedule.liquorStartTime;
          }
          earlyCutoffs.push(entry);
        }
      }
    }

    return { workingHours, earlyCutoffs };
  };

  /**
   * The API's error envelope carries `userMessage`; axios puts the body on `error.response.data`.
   * A 401 that survives the refresh interceptor means the session is gone — say so, don't show blanks.
   */
  const describeError = (err: unknown): string => {
    const e = err as { response?: { status?: number; data?: { userMessage?: string; message?: string } }; message?: string };
    const status = e?.response?.status;
    if (status === 401 || status === 403) return 'Your session has expired. Sign in again and reload this page.';
    return e?.response?.data?.userMessage || e?.response?.data?.message || e?.message || 'Request failed';
  };

  const fetchDefaults = async () => {
    setLoading(true);
    setLoadError(null);
    let data: any;
    try {
      ({ data } = await authInstance.get('/admin/stores/defaults'));
    } catch (err) {
      Logger.error('[SystemDefaultsEdit] Failed to load defaults:', err);
      setLoadError(describeError(err));
      setLoading(false);
      return;
    }

    if (!data || !data.defaults) {
      Logger.error('[SystemDefaultsEdit] Invalid API response - missing defaults:', data);
      setLoadError('The server returned no defaults. Reload this page; if it persists, tell engineering.');
      setLoading(false);
      return;
    }

    setDeliveryTiers(
      data.defaults.deliveryTiers || [{ maxKm: 999, deliveryCharge: '0', serviceFee: '19', minimumCartCheckout: '50', freeDeliveryAmount: null }]
    );

    // Transform schedule data
    const scheduleData = transformToScheduleData(
      data.defaultWorkingHours || [],
      data.defaultEarlyCutoffs || []
    );
    setSchedule(scheduleData);

    if (data.uberDeliveryDefaults) {
      setUberDropoffDefaults(data.uberDeliveryDefaults.dropoff || null);
      setUberReturnDefaults(data.uberDeliveryDefaults.return || null);
    }

    if (data.orderOperationsDefaults?.alertSettings) {
      const alert = data.orderOperationsDefaults.alertSettings;
      setAlertsEnabled(alert.alertsEnabled ?? true);
      setPickingSlaMinutes(String(alert.pickingSlaMinutes ?? 10));
      setPackingSlaMinutes(String(alert.packingSlaMinutes ?? 15));
      setPickupSlaMinutes(String(alert.pickupSlaMinutes ?? 30));
    }

    setCheckoutCutoffMinutes(String(data.defaults.checkoutCutoffMinutes ?? 30));

    setLoading(false);
  };

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      const parsedTiers = deliveryTiers.map((t) => ({
        maxKm: parseFloat(String(t.maxKm)),
        deliveryCharge: parseFloat(String(t.deliveryCharge)) || 0,
        serviceFee: parseFloat(String(t.serviceFee)) || 0,
        minimumCartCheckout: parseFloat(String(t.minimumCartCheckout)) || 0,
        freeDeliveryAmount: t.freeDeliveryAmount != null && String(t.freeDeliveryAmount) !== ''
          ? parseFloat(String(t.freeDeliveryAmount))
          : null,
      }));

      if (parsedTiers.some((t) => isNaN(t.maxKm) || t.maxKm <= 0)) {
        alert('All tiers must have a valid km distance greater than 0.');
        return;
      }

      try {
        await authInstance.put('/admin/stores/defaults', { deliveryTiers: parsedTiers });
      } catch (err) {
        alert(`Failed to save: ${describeError(err)}`);
        return;
      }
      alert('Pricing defaults saved!');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    const { workingHours, earlyCutoffs } = transformFromScheduleData(schedule);

    try {
      // Sequential, not Promise.all: a 401 on the first write must not let the second one through
      // (both would send blank arrays over the live defaults on a dead session).
      await authInstance.put('/admin/stores/defaults/working-hours', { workingHours });
      await authInstance.put('/admin/stores/defaults/early-cutoffs', { earlyCutoffs });
      alert('Schedule saved successfully!');
    } catch (err) {
      Logger.error('Failed to save schedule:', err);
      alert(`Failed to save schedule: ${describeError(err)}`);
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSaveAlertSettings = async () => {
    setSaving(true);
    try {
      await authInstance.put('/admin/stores/defaults/alert-settings', {
        alertsEnabled,
        pickingSlaMinutes: parseInt(pickingSlaMinutes) || 10,
        packingSlaMinutes: parseInt(packingSlaMinutes) || 15,
        pickupSlaMinutes: parseInt(pickupSlaMinutes) || 30,
      });
      alert('Alert settings saved!');
    } catch (err) {
      Logger.error('Failed to save alert settings:', err);
      alert(`Failed to save alert settings: ${describeError(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCutoffs = async () => {
    const minutes = Number(checkoutCutoffMinutes);
    if (checkoutCutoffMinutes.trim() === '' || !Number.isInteger(minutes) || minutes < 0 || minutes > 240) {
      alert('Cut off must be a whole number of minutes between 0 and 240.');
      return;
    }
    setSavingCutoffs(true);
    try {
      await authInstance.put('/admin/stores/defaults', { checkoutCutoffMinutes: minutes });
      alert('Cut offs saved. Every store now stops taking orders ' + (minutes === 0 ? 'at closing time.' : minutes + ' minutes before closing.'));
    } catch (err) {
      Logger.error('Failed to save cut offs:', err);
      alert(`Failed to save cut offs: ${describeError(err)}`);
    } finally {
      setSavingCutoffs(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          bgcolor: colors.bgDeepest,
        }}
      >
        <CircularProgress sx={{ color: colors.errorGlow }} />
      </Box>
    );
  }

  // A failed load must never render an editable form of blanks: "Apply to All Days" / "Reset" would
  // then post empty values over the live defaults that every store inherits.
  if (loadError) {
    return (
      <Box sx={{ p: 4, minHeight: '50vh', bgcolor: colors.bgDeepest }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchDefaults} data-testid="defaults-retry">
              Retry
            </Button>
          }
        >
          Couldn't load the system defaults — {loadError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      {/* Glassmorphism Header */}
      <Box sx={{ ...glassHeaderStyle, px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${colors.warning}15`,
                border: `1px solid ${colors.warning}30`,
              }}
            >
              <GlobalIcon sx={{ fontSize: 26, color: colors.warning }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: colors.textPrimary, letterSpacing: '-0.02em' }}>
                System Store Defaults
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Configure global defaults inherited by all stores
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/stores/command-center')}
            sx={{
              color: colors.textSecondary,
              borderColor: colors.border,
              '&:hover': { borderColor: colors.textPrimary, color: colors.textPrimary },
            }}
          >
            Back to Command Center
          </Button>
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            mt: 3,
            '& .MuiTabs-indicator': {
              bgcolor: colors.errorGlow,
              height: 3,
              borderRadius: 1.5,
            },
            '& .MuiTab-root': {
              color: colors.textMuted,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              minHeight: 48,
              '&.Mui-selected': {
                color: colors.textPrimary,
              },
              '&:hover': {
                color: colors.textSecondary,
              },
            },
          }}
        >
          <Tab icon={<ScheduleIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Schedule" />
          <Tab icon={<CartIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Pricing" />
          {/* Operations only holds the SLA settings, so it's hidden with them (Jason, 1 Nov 2026) */}
          {SHOW_SLA && <Tab icon={<OperationsIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Operations" />}
          <Tab icon={<DeliveryIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Delivery" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ px: 3 }}>
        {/* Schedule Tab */}
        <TabPanel value={activeTab} index={0}>
          <Paper
            sx={{
              p: 3,
              bgcolor: colors.bgCard,
              borderRadius: 3,
              border: `1px solid ${colors.border}`,
            }}
          >
            <SmartScheduleEditor
              schedule={schedule}
              globalSchedule={{}} // No inheritance for system defaults
              useGlobalDefaults={false} // Always custom for system defaults
              onScheduleChange={setSchedule}
              onUseGlobalDefaultsChange={() => { }} // No toggle for system defaults
              saving={savingSchedule}
              isSystemDefaults // Flag to hide the toggle
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <SaveButton onClick={handleSaveSchedule} saving={savingSchedule} label="Save Schedule" />
            </Box>
          </Paper>
        </TabPanel>

        {/* Pricing Tab */}
        <TabPanel value={activeTab} index={1}>
          <Paper
            sx={{
              p: 3,
              bgcolor: colors.bgCard,
              borderRadius: 3,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <CartIcon sx={{ color: colors.successGlow }} />
              <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                Pricing & Cart Defaults
              </Typography>
            </Box>

            <Alert
              severity="info"
              sx={{
                mb: 3,
                bgcolor: `${colors.info}08`,
                color: colors.textSecondary,
                border: `1px solid ${colors.info}20`,
                '& .MuiAlert-icon': { color: colors.info },
              }}
            >
              These values apply to stores with NULL settings. Stores with specific overrides will keep their custom values.
            </Alert>

            <DeliveryTierBuilder tiers={deliveryTiers} onChange={setDeliveryTiers} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <SaveButton onClick={handleSavePricing} saving={saving} label="Save Pricing Defaults" />
            </Box>
          </Paper>
        </TabPanel>

        {/* Operations Tab (SLA config — hidden behind SHOW_SLA feature flag) */}
        <TabPanel value={activeTab} index={SHOW_SLA ? 2 : -1}>
          {SHOW_SLA && (
          <Paper
            sx={{
              p: 3,
              bgcolor: colors.bgCard,
              borderRadius: 3,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <AlertIcon sx={{ color: colors.warning }} />
              <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                SLA Alert Settings
              </Typography>
            </Box>

            <Alert
              severity="info"
              sx={{
                mb: 3,
                bgcolor: `${colors.info}08`,
                color: colors.textSecondary,
                border: `1px solid ${colors.info}20`,
                '& .MuiAlert-icon': { color: colors.info },
              }}
            >
              Configure SLA alert thresholds. Alerts notify managers when orders sit too long in each state.
            </Alert>

            <Box sx={{ mb: 3 }}>
              <PlateToggle
                label="Enable SLA Alerts by Default"
                description="New stores will have alerts enabled"
                checked={alertsEnabled}
                onChange={setAlertsEnabled}
              />
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <PremiumInput
                  label="Picking SLA"
                  value={pickingSlaMinutes}
                  onChange={setPickingSlaMinutes}
                  type="number"
                  suffix="min"
                  helperText="Alert if PLACED too long"
                  disabled={!alertsEnabled}
                  min={1}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <PremiumInput
                  label="Packing SLA"
                  value={packingSlaMinutes}
                  onChange={setPackingSlaMinutes}
                  type="number"
                  suffix="min"
                  helperText="Alert if PICKING too long"
                  disabled={!alertsEnabled}
                  min={1}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <PremiumInput
                  label="Pickup SLA"
                  value={pickupSlaMinutes}
                  onChange={setPickupSlaMinutes}
                  type="number"
                  suffix="min"
                  helperText="Alert if PACKED too long"
                  disabled={!alertsEnabled}
                  min={1}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <SaveButton onClick={handleSaveAlertSettings} saving={saving} label="Save Alert Settings" />
            </Box>
          </Paper>
          )}
        </TabPanel>

        {/* Delivery Tab */}
        <TabPanel value={activeTab} index={SHOW_SLA ? 3 : 2}>
          {/* What each courier is asked to verify, per step (Jason, 1 Nov 2026). Read-only: these
              mirror what the backend sends on every booking (uber-direct-delivery-service.js,
              loop-delivery-service.js), so change them there, not here. */}
          <DeliveryDefaults
            title="Uber Direct Delivery Defaults"
            info="Standard Uber Direct verification settings applied to all deliveries. These are not configurable — they reflect what Uber enforces."
            columns={[
              { title: 'At Pickup/Collection', rows: [
                { label: 'Photo Proof', description: 'Courier photographs the order at collection' },
              ] },
              { title: 'At Dropoff/Delivery', rows: [
                { label: 'PIN Code', description: 'Uber sends the customer a 4-digit PIN via SMS' },
                { label: 'ID Check', tag: 'Restricted Items', description: 'All orders are marked restricted - courier verifies customer is 18+' },
                { label: 'Signature', tag: 'Restricted Items', description: 'Uber requires a signature on orders marked as containing alcohol' },
              ] },
              { title: 'Returned Deliveries', rows: [
                { label: 'PIN Code', description: "Store confirms the return with the customer's PIN" },
                { label: 'Photo Proof', description: 'Courier photographs the returned package' },
              ] },
            ]}
            note={<><strong>Note:</strong> PIN codes are generated by Uber (the last 4 digits of the customer's phone) and can't be set by us. Marking an order as containing alcohol makes Uber require the ID check and signature. A failed delivery comes back as a separate Uber return job using the same PIN.</>}
          />
          <Box sx={{ mt: 3 }}>
            <DeliveryDefaults
              title="Loop Delivery Defaults"
              info="Standard Loop (Takealot Driver Service) verification settings applied to all deliveries. These are not configurable — they reflect what we send Loop on every booking."
              columns={[
                { title: 'At Pickup/Collection', rows: [
                  { label: 'PIN Code', description: "Store gives the driver the order's collection code" },
                  { label: 'Photo Proof', description: 'Driver photographs the order at collection' },
                ] },
                { title: 'At Dropoff/Delivery', rows: [
                  { label: 'PIN Code', description: 'Customer gives the driver their 4-digit delivery PIN' },
                  { label: 'ID Check', tag: 'Restricted Items', description: 'All orders are marked restricted - courier verifies customer is 18+' },
                ] },
                { title: 'Returned Deliveries', rows: [
                  { label: 'PIN Code', description: 'Store confirms the return with its own return code' },
                  { label: 'Photo Proof', description: 'Driver photographs the returned package' },
                ] },
              ]}
              note={<><strong>Note:</strong> Loop uses three separate codes, all generated by Deeliver: collection (store staff), delivery (customer) and return (store staff). Each is shown only to the person who hands the order over.</>}
            />
          </Box>

          {/* Cut Offs (Jason, 2 Nov 2026): one number instead of shaving every store's closing
              time. The server applies it to the hours it sends the app and tablets and to its
              own open-or-closed decision, so the stored hours stay the shop's real hours. */}
          <Paper
            sx={{
              p: 3,
              mt: 3,
              bgcolor: colors.bgCard,
              borderRadius: 3,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <ScheduleIcon sx={{ color: colors.warning }} />
              <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                Cut Offs
              </Typography>
            </Box>

            <Alert
              severity="info"
              sx={{
                mb: 3,
                bgcolor: `${colors.info}08`,
                color: colors.textSecondary,
                border: `1px solid ${colors.info}20`,
                '& .MuiAlert-icon': { color: colors.info },
              }}
            >
              Every store stops taking new orders this many minutes before its closing time. From that moment the
              app shows the store as closed, checkout refuses, and the order tablet goes to Closed. Orders already
              on the tablet carry on as normal. Store hours stay exactly as entered; nothing needs shortening per store.
            </Alert>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <PremiumInput
                  label="Stop taking orders before closing"
                  value={checkoutCutoffMinutes}
                  onChange={setCheckoutCutoffMinutes}
                  type="number"
                  suffix="min"
                  helperText="Default 30. Set 0 to take orders right up to closing time."
                  min={0}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <SaveButton onClick={handleSaveCutoffs} saving={savingCutoffs} label="Save Cut Offs" />
            </Box>
          </Paper>
        </TabPanel>
      </Box>
    </Box>
  );
}
